<script setup lang="ts">
import { formatMoneyFromPence } from '@/helpers/moneyDisplay'
import { useCombinedStatementTotals } from '@/composables/useCombinedStatementTotals'
import StatementTaxResponsibilityNotice from '@/components/dashboard/statements/StatementTaxResponsibilityNotice.vue'
import DashboardHint from '@/components/dashboard/ui/DashboardHint.vue'
import type { DonorFinancialYear } from '@/types/donorFinancialYear'

const props = defineProps<{
  financialYear: DonorFinancialYear
}>()

const {
  showAddedSummary,
  addedTotalPence,
  addedGiftAidPence,
  combinedDonatedPence,
  combinedGiftAidPence,
} = useCombinedStatementTotals(
  () => props.financialYear.period,
  () => props.financialYear
)
</script>

<template>
  <div class="sa-stmt-totals">
    <article
      v-if="showAddedSummary"
      class="sa-stmt-combined-hero"
      aria-labelledby="stmt-combined-totals-title"
    >
      <div class="sa-stmt-combined-hero__hd">
        <h3 id="stmt-combined-totals-title" class="sa-stmt-combined-hero__title">
          {{ $t('Dashboard.Statements.Totals.CombinedTotalsLabel') }}
        </h3>
        <DashboardHint
          placement="top"
          :trigger-label="$t('Dashboard.Statements.Totals.CombinedTotalsHintTrigger')"
        >
          <i18n-t keypath="Dashboard.Statements.Totals.CombinedTotalsHintBody" tag="span">
            <template #swiftaidPanel>
              <strong>{{ $t('Dashboard.Statements.Totals.SwiftaidTitle') }}</strong>
            </template>
            <template #addedPanel>
              <strong>{{ $t('Dashboard.Statements.Totals.AddedPanelTitle') }}</strong>
            </template>
          </i18n-t>
        </DashboardHint>
      </div>
      <dl class="sa-stmt-combined-hero__stats">
        <div>
          <dt class="sa-stmt-stat-label">
            {{ $t('Dashboard.Home.TotalDonated') }}
          </dt>
          <dd class="sa-stmt-combined-hero__value">
            {{ formatMoneyFromPence(combinedDonatedPence) }}
          </dd>
        </div>
        <div>
          <dt class="sa-stmt-stat-label">
            {{ $t('Dashboard.Statements.Totals.CombinedGiftAidLabel') }}
          </dt>
          <dd class="sa-stmt-combined-hero__value">
            {{ formatMoneyFromPence(combinedGiftAidPence) }}
          </dd>
        </div>
      </dl>
    </article>

    <article class="sa-stmt-totals-card">
      <h3 class="sa-stmt-totals-card__title">
        {{ $t('Dashboard.Statements.Totals.SwiftaidTitle') }}
      </h3>
      <dl class="sa-stmt-totals-card__stats">
        <div>
          <dt class="sa-stmt-stat-label">
            {{ $t('Dashboard.Statements.Totals.SwiftaidGiftAidedDonations') }}
          </dt>
          <dd class="sa-stmt-stat-value">
            {{ formatMoneyFromPence(financialYear.totalDonations) }}
          </dd>
        </div>
        <div>
          <dt class="sa-stmt-stat-label">
            {{ $t('Dashboard.Statements.Totals.SwiftaidMaximumGiftAid') }}
          </dt>
          <dd class="sa-stmt-stat-value">
            {{ formatMoneyFromPence(financialYear.totalGiftAid) }}
          </dd>
        </div>
      </dl>
      <div class="sa-stmt-totals-card__footnotes">
        <p class="sa-stmt-totals-card__exclusion">
          <i18n-t keypath="Dashboard.Statements.Totals.SwiftaidExclusion" tag="span">
            <template #total>
              <strong class="sa-stmt-totals-card__exclusion-em">
                {{ formatMoneyFromPence(financialYear.totalDonations) }}
              </strong>
            </template>
            <template #giftAid>
              <strong class="sa-stmt-totals-card__exclusion-em">
                {{ formatMoneyFromPence(financialYear.totalGiftAid) }}
              </strong>
            </template>
          </i18n-t>
        </p>
        <StatementTaxResponsibilityNotice />
      </div>
    </article>

    <article
      v-if="showAddedSummary"
      class="sa-stmt-totals-card sa-stmt-totals-card--added"
      aria-labelledby="stmt-added-title"
    >
      <h3 id="stmt-added-title" class="sa-stmt-totals-card__title">
        {{ $t('Dashboard.Statements.Totals.AddedPanelTitle') }}
      </h3>
      <dl class="sa-stmt-totals-card__stats">
        <div>
          <dt class="sa-stmt-stat-label">{{ $t('Dashboard.Home.TotalDonated') }}</dt>
          <dd class="sa-stmt-stat-value">
            {{ formatMoneyFromPence(addedTotalPence) }}
          </dd>
        </div>
        <div>
          <dt class="sa-stmt-stat-label">{{ $t('Dashboard.Home.GiftAid') }}</dt>
          <dd class="sa-stmt-stat-value">
            {{ formatMoneyFromPence(addedGiftAidPence) }}
          </dd>
        </div>
      </dl>
    </article>
  </div>
</template>
